import React from 'react';
import { motion } from 'framer-motion';
import { Moon, Calendar as CalendarIcon, Info, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { AdSlot } from '../components/ads/AdSlot';
interface MoonCalendarPageProps {
  onNavigate: (page: string) => void;
}
export function MoonCalendarPage({
  onNavigate
}: MoonCalendarPageProps) {
  const currentDate = new Date().toLocaleDateString('ru-RU', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-25539">
      {/* Header */}
      <div className="bg-slate-900 text-white pt-6 pb-16 relative overflow-hidden" data-id="element-25540">
        <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-25541" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" data-id="element-25542" />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-25543">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Лунный календарь'
        }]} className="mb-8 text-slate-300" data-id="element-25544" />

          <div className="flex flex-col md:flex-row items-center justify-between gap-8" data-id="element-25545">
            <div className="max-w-2xl" data-id="element-25546">
              <Badge variant="pro" className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 mb-4" data-id="element-25547">
                Обновляется ежедневно
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-id="element-25548">
                Лунный календарь
              </h1>
              <p className="text-xl text-slate-300 mb-2 capitalize" data-id="element-25549">
                {currentDate}
              </p>
              <p className="text-slate-400" data-id="element-25550">
                Следите за фазами Луны, чтобы планировать важные дела, стрижки,
                посадки и понимать общий энергетический фон дня.
              </p>
            </div>

            <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 120,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-48 h-48 rounded-full bg-gradient-to-br from-slate-100 to-slate-400 shadow-[0_0_50px_rgba(255,255,255,0.2)] flex items-center justify-center relative overflow-hidden" data-id="element-25551">
              {/* Simple moon phase representation (Waxing Gibbous) */}
              <div className="absolute inset-0 bg-slate-900 rounded-full transform translate-x-1/4 scale-110 opacity-80 mix-blend-multiply" data-id="element-25552" />
              <div className="absolute inset-0 shadow-inner rounded-full" data-id="element-25553" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 -mt-8 relative z-20" data-id="element-25554">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-25555">
          {/* Left Column: Current Status */}
          <div className="lg:col-span-2 space-y-8" data-id="element-25556">
            <Card className="p-6 sm:p-8 shadow-xl border-0" data-id="element-25557">
              <h2 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-25558">
                Энергия сегодняшнего дня
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-25559">
                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25560">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25561">
                    Фаза Луны
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25562">
                    Растущая Луна <span className="text-indigo-500" data-id="element-25563">🌔</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25564">
                    Освещенность: 78%
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25565">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25566">
                    Лунный день
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25567">
                    11-й лунный день
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25568">
                    Символ: Огненный меч
                  </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100" data-id="element-25569">
                  <div className="text-sm text-slate-500 font-medium mb-1" data-id="element-25570">
                    Луна в знаке
                  </div>
                  <div className="text-xl font-bold text-slate-900 flex items-center gap-2" data-id="element-25571">
                    Лев <span className="text-amber-500" data-id="element-25572">♌</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25573">
                    Стихия: Огонь
                  </div>
                </div>

                <div className="bg-rose-50 rounded-2xl p-5 border border-rose-100" data-id="element-25574">
                  <div className="text-sm text-rose-600 font-medium mb-1 flex items-center gap-1" data-id="element-25575">
                    <AlertCircle className="w-4 h-4" data-id="element-25576" /> Внимание
                  </div>
                  <div className="text-xl font-bold text-slate-900" data-id="element-25577">
                    Луна без курса
                  </div>
                  <div className="text-sm text-slate-600 mt-2" data-id="element-25578">
                    С 14:30 до 17:45 (МСК)
                  </div>
                </div>
              </div>

              <div className="prose prose-slate max-w-none" data-id="element-25579">
                <p data-id="element-25580">
                  Один из самых энергетически мощных дней лунного месяца.
                  Энергия бьет ключом, пробуждаются скрытые резервы организма.
                  Отличный день для активных действий, начала новых проектов и
                  физических нагрузок.
                </p>
                <p data-id="element-25581">
                  Луна во Льве добавляет желания быть в центре внимания, творить
                  и радовать себя. Однако в период "Луны без курса" (с 14:30 до
                  17:45) лучше воздержаться от принятия важных решений и
                  подписания документов — дела, начатые в это время, могут пойти
                  не по плану.
                </p>
              </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-25582">
              <Card className="p-6 border-emerald-200 bg-emerald-50/30" data-id="element-25583">
                <div className="flex items-center gap-2 mb-4" data-id="element-25584">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" data-id="element-25585" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-25586">
                    Благоприятно
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-25587">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25588">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25589">•</span>{' '}
                    Творческие начинания
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25590">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25591">•</span>{' '}
                    Физическая активность и спорт
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25592">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25593">•</span> Стрижка
                    волос (для быстрого роста)
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25594">
                    <span className="text-emerald-500 mt-0.5" data-id="element-25595">•</span> Покупки
                    одежды и украшений
                  </li>
                </ul>
              </Card>

              <Card className="p-6 border-rose-200 bg-rose-50/30" data-id="element-25596">
                <div className="flex items-center gap-2 mb-4" data-id="element-25597">
                  <XCircle className="w-6 h-6 text-rose-500" data-id="element-25598" />
                  <h3 className="text-lg font-bold text-slate-900" data-id="element-25599">
                    Не рекомендуется
                  </h3>
                </div>
                <ul className="space-y-3" data-id="element-25600">
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25601">
                    <span className="text-rose-500 mt-0.5" data-id="element-25602">•</span> Пассивность
                    и лень
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25603">
                    <span className="text-rose-500 mt-0.5" data-id="element-25604">•</span>{' '}
                    Хирургические операции на сердце
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25605">
                    <span className="text-rose-500 mt-0.5" data-id="element-25606">•</span> Важные дела
                    в период "Луны без курса"
                  </li>
                  <li className="flex items-start gap-2 text-sm text-slate-700" data-id="element-25607">
                    <span className="text-rose-500 mt-0.5" data-id="element-25608">•</span> Конфликты с
                    начальством
                  </li>
                </ul>
              </Card>
            </div>
          </div>

          {/* Right Column: Mini Calendar & Info */}
          <div className="space-y-6" data-id="element-25609">
            <Card className="p-6" data-id="element-25610">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-25611">
                <CalendarIcon className="w-5 h-5 text-indigo-500" data-id="element-25612" />
                Ближайшие фазы
              </h3>
              <div className="space-y-4" data-id="element-25613">
                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25614">
                  <div className="flex items-center gap-3" data-id="element-25615">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-25616">
                      🌕
                    </div>
                    <div data-id="element-25617">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25618">
                        Полнолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25619">в Деве</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25620">
                    24 фев
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25621">
                  <div className="flex items-center gap-3" data-id="element-25622">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-lg" data-id="element-25623">
                      🌗
                    </div>
                    <div data-id="element-25624">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25625">
                        Третья четверть
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25626">в Стрельце</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25627">
                    3 мар
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-id="element-25628">
                  <div className="flex items-center gap-3" data-id="element-25629">
                    <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-lg" data-id="element-25630">
                      🌑
                    </div>
                    <div data-id="element-25631">
                      <div className="text-sm font-bold text-slate-900" data-id="element-25632">
                        Новолуние
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-25633">в Рыбах</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-slate-700" data-id="element-25634">
                    10 мар
                  </div>
                </div>
              </div>
            </Card>

            <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100" data-id="element-25635">
              <div className="flex items-start gap-3" data-id="element-25636">
                <Info className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" data-id="element-25637" />
                <div data-id="element-25638">
                  <h4 className="font-bold text-indigo-900 mb-2" data-id="element-25639">
                    Что такое Луна без курса?
                  </h4>
                  <p className="text-sm text-indigo-800/80 leading-relaxed" data-id="element-25640">
                    Это период, когда Луна сделала свой последний мажорный
                    аспект в одном знаке зодиака, но еще не перешла в следующий.
                    В это время дела часто идут впустую, планы срываются, а
                    начатые проекты не приносят ожидаемого результата. Лучше
                    посвятить это время рутине и отдыху.
                  </p>
                </div>
              </div>
            </div>

            {/* Cross-links */}
            <div className="space-y-3" data-id="element-25641">
              <button onClick={() => onNavigate('daily-horoscope')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-25642">
                <span className="text-xl" data-id="element-25643">☀️</span>
                <div data-id="element-25644">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-25645">
                    Гороскоп на сегодня
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25646">
                    Прогноз для вашего знака
                  </div>
                </div>
              </button>
              <button onClick={() => onNavigate('calendar')} className="flex items-center gap-3 w-full p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all text-left group" data-id="element-25647">
                <span className="text-xl" data-id="element-25648">📅</span>
                <div data-id="element-25649">
                  <div className="text-sm font-bold text-slate-900 group-hover:text-indigo-600 transition-colors" data-id="element-25650">
                    Астро-календарь
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25651">
                    Затмения и ретрограды
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Ad Banner */}
        <div className="mt-8" data-id="element-25652">
          <AdSlot slotId="SLOT-11" onNavigate={onNavigate} data-id="element-25653" />
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-br from-slate-900 to-indigo-950 rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden shadow-xl" data-id="element-25654">
          <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-25655" />
          <div className="relative z-10" data-id="element-25656">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" data-id="element-25657">
              Узнайте, как Луна влияет именно на вас
            </h2>
            <p className="text-indigo-200 mb-8 max-w-xl mx-auto" data-id="element-25658">
              Постройте свою натальную карту и получите персональный анализ
              лунных транзитов.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-25659">
              <button onClick={() => onNavigate('service-natal')} className="px-8 py-3 bg-white text-slate-900 rounded-xl font-semibold hover:bg-slate-100 transition-colors" data-id="element-25660">
                Заказать натальную карту
              </button>
              <button onClick={() => onNavigate('free-chart')} className="px-8 py-3 border border-white/20 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors" data-id="element-25661">
                Бесплатная карта
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}